import {
  HtmlHTMLAttributes,
  PropsWithChildren,
  createElement,
  useRef,
  useEffect,
  useMemo
} from 'react';
import classnames from 'classnames';
import {
  Card,
  CssBaseline,
  Theme,
  ThemeProvider,
  createTheme,
  makeStyles
} from '@material-ui/core';
import { Notification, TitleComponent, useCheckAuth } from 'react-admin';
import { StaticContext } from 'react-router';
import { useHistory } from 'react-router-dom';

import { LoginForm, theme as defaultTheme } from '.';
import logo from '../assets/images/logo.svg';

interface LoginProps extends Omit<HtmlHTMLAttributes<HTMLDivElement>, 'title'> {
  backgroundImage?: string;
  classes?: object;
  className?: string;
  notification?: any;
  staticContext?: StaticContext;
  theme?: object;
  title?: TitleComponent;
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    main: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      height: '1px',
      alignItems: 'center',
      justifyContent: 'flex-start',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundImage:
        'radial-gradient(circle at 50% 14em, #710e12 0%, #2d0507 60%, #2d0507 100%)'
    },
    card: {
      minWidth: 300,
      marginTop: '6em'
    },
    icon: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      padding: '1em'
    }
  }),
  { name: 'RaLogin' }
);

export default function LoginPage(props: PropsWithChildren<LoginProps>) {
  const {
    backgroundImage,
    classes: classesOverride,
    className,
    children,
    notification = Notification,
    staticContext,
    theme = defaultTheme,
    title,
    ...rest
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);

  const classes = useStyles(props);

  const checkAuth = useCheckAuth();
  const history = useHistory();

  useEffect(() => {
    checkAuth({}, false)
      .then(() => {
        history.push('/');
      })
      .catch(error => {
        console.error(error);
      });
  }, [checkAuth, history]);

  let backgroundImageLoaded = false;

  const updateBackgroundImage = () => {
    if (!backgroundImageLoaded && containerRef.current) {
      containerRef.current.style.backgroundImage = `url(${backgroundImage})`;
      backgroundImageLoaded = true;
    }
  };

  const lazyLoadBackgroundImage = () => {
    if (backgroundImage) {
      const img = new Image();
      img.onload = updateBackgroundImage;
      img.src = backgroundImage;
    }
  };

  useEffect(() => {
    if (!backgroundImageLoaded) {
      lazyLoadBackgroundImage();
    }
  });

  const muiTheme = useMemo(() => createTheme(theme), [theme]);

  return (
    <ThemeProvider theme={muiTheme}>
      <div
        className={classnames(classes.main, className)}
        {...rest}
        ref={containerRef}
      >
        <Card className={classes.card}>
          <div className={classes.icon}>
            <img src={logo} alt="Brasal Refrigerantes" />
          </div>
          <CssBaseline />
          <LoginForm />
        </Card>
        {notification ? createElement(notification) : null}
      </div>
    </ThemeProvider>
  );
}
